import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Col, Dropdown, Modal, Row, Table, Tag, Tooltip } from 'antd';
import { CloudDownloadOutlined, FilterFilled, UnorderedListOutlined } from '@ant-design/icons';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import scrollIntoView from 'scroll-into-view';
import { useAllOffersVersions } from 'useSWR';
import { useInitialValueMaker, usePushFilteredUrl, useWindowSize } from 'hooks';
import { exportTable, STEP_DEF } from 'utils';
import { CircularProgress, Input, Title } from 'components';
import { FilterModal, Order, TableAlert } from 'components/ArticlesTable';
import { setOfferTable, setShouldScroll, setTableOrder } from 'slices/commonSlice';
import { higherCommittee } from 'constant/static';
import { isDisposalPaymentFeatureEnabled } from 'constant/featureFlags';
import { TABLE_STICKY } from 'constant/table';
import DecisionAskedModal from 'components/Modal/DecisionAsked';
import { useGetCommitteeQuery, useGetCurrentUserQuery, useGetFilteredUserListQuery } from 'services';

import convertToExportUrl from '../../utils/convertToExportUrl';

const urlMatcher = ['tum-maddeler', 'sorumlu-olduklarim'];
const OFFER_VERSIONS_PATH = '/article_offer_versions';

const filterNames = {
  otherCommittees: 'Teklif Edilen İlim Heyeti',
  owner: 'Formu Dolduran',
  ownerCommittee: 'Sorumlu İlim Heyeti',
  dateRange: 'Tarih Aralığı',
  committeeContributions: 'Katkıda Bulunacak İlim Heyeti',
  status: 'Genel Durum',
};

const { Search } = Input;

const renderAuthors = (author) => {
  return author.map((a, i, { length }) => (
    <div key={a && a.id}>
      {a.author.user?.fullName}
      {length - 1 !== i && ','}
    </div>
  ));
};

const Articles = () => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { formatMessage } = useIntl();
  const { width, height } = useWindowSize();
  const { id } = useParams();
  const { data: current_user } = useGetCurrentUserQuery();
  const [decisionModal, setDecisionModal] = useState({
    visible: false,
    data: null,
  });
  const [searchParams] = useSearchParams();

  const [searchOwner, setSearchOwner] = useState('');

  const shouldScroll = useSelector(({ common }) => common.offerShouldTableScroll);
  const tableOrder = useSelector(({ common }) => common.tableOrder);
  const scrollID = useSelector(({ common }) => common.offerID);

  const { data: ownerOptions } = useGetFilteredUserListQuery({
    fullName: searchOwner,
  });

  const { isAdmin, isOrderUnit, onCommittee } = current_user;

  const urlFilter = pathname.split('/');
  // bu koşullarda veriler versiondan değil ana veriden geldiği için tablodaki dataindex lerin değiştirilmesi için kullanılacak condition
  const isDraft = urlFilter.includes('taslak-maddeler');
  const isAskDecision = urlFilter.includes('gorus-istenen-maddeler');
  const afterCopyrigt = urlFilter.includes('telif-sonrasi');
  const assignToMe = urlFilter.includes('bana-atananlar');
  const control = urlFilter.includes('kontrol-edilecekler');
  const euUnit = urlFilter.includes('ansiklopedi-birimi');
  const pubUnit = urlFilter.includes('yayin-mudurlugu');
  const supervisorControl = urlFilter.includes('sorumlu-olduklarim');

  const stringId = id ?? !urlMatcher.includes(urlFilter[2]) ? urlFilter[2] : '';

  const afterCopyrightCommitee = useMemo(() => {
    if (euUnit) {
      return 'eu';
    }
    if (pubUnit) {
      return 'pub';
    }
  }, [euUnit, pubUnit]);

  const commiteeID = useMemo(() => {
    if (assignToMe || control || supervisorControl) {
      return '';
    }
    return stringId;
  }, [assignToMe, control, stringId, supervisorControl]);

  let subMenus,
    extraUrl = '',
    subTitle = '';

  if (urlFilter.includes('teklif-edilen-maddeler')) {
    subMenus = `&otherCommittees.id=${stringId}`;
    subTitle = formatMessage({ id: 'sidebar.offeredArticles' });
  }
  if (urlFilter.includes('sorumlu-olunan-maddeler')) {
    subMenus = '';
    subTitle = formatMessage({ id: 'sidebar.ownerArticles' });
  }
  if (urlFilter.includes('gorus-istenen-maddeler')) {
    subMenus = `&opinionCommittee=${stringId}`;
    subTitle = formatMessage({ id: 'sidebar.askDecisionArticles' });
  }
  if (urlFilter.includes('heyetten-cikan-maddeler')) {
    subMenus = `&extracted=${stringId}`;
    subTitle = formatMessage({ id: 'sidebar.removedFromCommitteeArticles' });
  }
  if (afterCopyrigt) {
    extraUrl = 'post_orders';
    subTitle = formatMessage({ id: 'sidebar.offeredArticles' });
  }

  if (supervisorControl) {
    extraUrl = 'supervisor';
    subTitle = formatMessage({ id: 'sidebar.articleSupervisor' });
  }
  if (assignToMe) {
    extraUrl = `post_orders/${afterCopyrightCommitee}/my_assignments`;
    subTitle = 'Bana Atananlar';
  }
  if (control) {
    extraUrl = `post_orders/${afterCopyrightCommitee}/my_assignments`;
    subTitle = 'Kontrol Edilecekler';
  }
  if (isDraft) {
    subMenus = `&isDraft=true`;
    subTitle = formatMessage({ id: 'sidebar.draftArticles' });
  }

  const { page, pageSize, title, filterModal: filterModals, filterModalURL, filterStatusObjects, filterStatusURL } = useInitialValueMaker(search);
  const filtersRef = useRef();
  const debounce = useRef();

  // response alanlarina versiona gore mi ulasilacaginin kontrolu bu deger false ise versiona gore ulasiliyor demektir
  const isNotVersionData = isDraft || isAskDecision || afterCopyrigt || assignToMe || control || supervisorControl;

  const handleDeliveryDate = useMemo(() => {
    if (isNotVersionData && supervisorControl) {
      return ['supervisorOpinion', 'finishDate'];
    } else if (isNotVersionData) {
      return ['ouDate', 'deliveryDate'];
    } else {
      return ['targetOffer', 'ouDate', 'deliveryDate'];
    }
  }, [isNotVersionData, supervisorControl]);

  const handleSupervisorDeliveryDateTitle = useMemo(() => {
    if (supervisorControl) {
      return <FormattedMessage id="article.filter.supervisor.deliveryDate" />;
    } else {
      return <FormattedMessage id="article.filter.deliveryDate" />;
    }
  }, [supervisorControl]);

  const [urlFilters, setUrlFilters] = useState({
    page: page || 1,
    pageSize: pageSize || 20,
    step: afterCopyrigt ? 4 : '',
    title: title ?? '',
    filterModalURL: filterModalURL ?? '',
    filterModalObjects: filterModals ?? {},
    filterStatusURL: filterStatusURL ?? '',
    filterStatusObjects: filterStatusObjects ?? {},
    filterTags: [],
    filteredURL: search.replaceAll('?', ''),
  });

  const { newRoute } = usePushFilteredUrl(urlFilters, pathname);
  const [loading, setLoading] = useState(false);
  const [wait, setWait] = useState(false);
  const [order, setOrder] = useState({ type: tableOrder.type, order: tableOrder.order });
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { data: committees, isLoading: titleLoading } = useGetCommitteeQuery(id, { skip: !id || supervisorControl });

  const {
    offers,
    isLoading,
    url: downloadURL,
  } = useAllOffersVersions({
    subMenus,
    opinion: control ? 'pending' : null,
    extraUrl,
    page: searchParams.get('page') || 1,
    committeeId: commiteeID,
    pageSize: searchParams.get('pageSize') || 20,
    orderType: order,
    filtered: newRoute ?? urlFilters.filterModalURL + urlFilters.filterStatusURL,
  });

  const searchName = (text) => {
    setLoading(true);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setUrlFilters((prevState) => ({ ...prevState, title: text, page: 1 }));
      setLoading(false);
    }, 300);
  };

  const clearFilters = () => {
    if (filtersRef.current) {
      filtersRef.current.clearFilters();
    } else {
      // Clear filter modal values
      localStorage.removeItem('filterModalValues');
      setUrlFilters((prevState) => ({
        ...prevState,
        page: 1,
        filterModalURL: '',
        filterStatusURL: '',
      }));
    }
  };
  const columns = [
    {
      title: <FormattedMessage id="article.detail.FormNumber" />,
      dataIndex: isNotVersionData ? 'formNumber' : 'targetOffer',
      width: 90,
      align: 'center',
      render: (a) => <span>{a?.formNumber ?? a}</span>,
      fixed: width > 575 ? 'left' : null,
    },
    {
      title: (
        <Order
          setOrder={setOrder}
          order={order}
          title={<FormattedMessage id="article.detail.articleTitle" />}
          type="title"
        />
      ),
      className: order.type === 'title' && order.order !== null ? 'active-order' : newRoute.includes('title') && 'active-filter-column',
      dataIndex: 'title',
      width: 240,
      render: (article, record) => {
        const hasRelationMembership = isNotVersionData ? record?.hasRelationMembership : record?.targetOffer?.hasRelationMembership;

        return (
          <div
            className="gx-d-flex gx-align-items-center"
            style={{ gap: 4 }}
          >
            <Link
              style={{ lineHeight: '22px' }}
              to={`/madde-detay/${isNotVersionData ? record.id : record.targetOffer?.id}`}
            >
              {article}
            </Link>
            {hasRelationMembership ? (
              <Tooltip title="Gruba Dahil">
                <Tag
                  bordered={false}
                  className="gx-m-0"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingInline: 4,
                    lineHeight: 1,
                  }}
                >
                  <UnorderedListOutlined
                    style={{
                      fontSize: 12,
                      color: 'var(--ant-color-link, #1677ff)',
                    }}
                  />
                </Tag>
              </Tooltip>
            ) : null}
          </div>
        );
      },
      fixed: width > 575 ? 'left' : null,
    },
    {
      title: <FormattedMessage id="article.filter.offeredAuthor" />,
      dataIndex: 'authors',
      width: 110,
      align: 'center',
      className: newRoute.includes('author=') && 'active-filter-column',
      hidden: !(isAdmin || isOrderUnit || onCommittee),
      render: (author) => (author ? renderAuthors(author) : '-'),
    },
    {
      title: (
        <Order
          title={<FormattedMessage id="article.filter.date" />}
          type="createdAt"
          setOrder={setOrder}
          order={order}
        />
      ),
      dataIndex: 'createdAt',
      align: 'center',
      width: 140,
      className: order.type === 'createdAt' && order.order !== null ? 'active-order' : newRoute.includes('createdAt') && 'active-filter-column',
      render: (updatedAt) => dayjs(updatedAt).format('DD/MM/YYYY - HH:mm'),
    },
    {
      title: (
        <Order
          title={<FormattedMessage id="article.filter.author.deliveryDate" />}
          type="deliveryDate"
          setOrder={setOrder}
          order={order}
        />
      ),
      dataIndex: ['ouDate', 'deliveryDate'],
      hidden: !supervisorControl,
      align: 'center',
      width: 140,
      className: order.type === 'deliveryDate' && order.order !== null ? 'active-order' : newRoute.includes('deliveryDate') && 'active-filter-column',
      render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
    },
    {
      title: (
        <Order
          title={handleSupervisorDeliveryDateTitle}
          type={supervisorControl ? 'soFinishDate' : 'deliveryDate'}
          setOrder={setOrder}
          order={order}
        />
      ),
      dataIndex: handleDeliveryDate,
      align: 'center',
      width: 140,
      className: order.type === 'deliveryDate' && order.order !== null ? 'active-order' : newRoute.includes('deliveryDate') && 'active-filter-column',
      render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
    },
    {
      title: <FormattedMessage id="article.detail.ownerCommittee" />,
      dataIndex: 'ownerCommittee',
      align: 'center',
      width: 130,
      className: newRoute.includes('ownerCommittee') && 'active-filter-column',
      render: (ownerCommittee) => <span>{isDraft ? committees?.name : ownerCommittee?.name && ownerCommittee?.name}</span>,
    },
    {
      title: <FormattedMessage id="article.filter.generalProcess" />,
      dataIndex: isNotVersionData ? 'step' : ['targetOffer', 'step'],
      align: 'center',
      width: 130,
      render: (step) => (
        <p className="gx-py-2 gx-m-0">
          <FormattedMessage id={step ? `article.${STEP_DEF[step]}.title` : `article.${STEP_DEF[1]}.title`} />
        </p>
      ),
    },
    {
      title: <FormattedMessage id="article.filter.generalSituation" />,
      dataIndex: isNotVersionData ? 'status' : 'targetOffer',
      className: `${newRoute.includes('status') && 'active-filter-column'} article-table-general-situation`,
      align: 'center',
      width: 130,
      render: (a) => (
        <>
          <TableAlert karar={isNotVersionData ? a : a && a.status} />
        </>
      ),
    },
  ].filter((item) => !item.hidden);

  const pagination = {
    defaultCurrent: urlFilters.page,
    current: page,
    defaultPageSize: urlFilters.pageSize || 20,
    total: offers?.['hydra:totalItems'] ?? 1,
    showTotal: (total) =>
      `${formatMessage(
        {
          id: 'table.totalItem',
        },
        { type: 'Madde' }
      )} ${total}`,
    position: ['bottomRight'],
    responsive: true,
    size: 'default',
    onChange: (page, pageSize) => {
      setUrlFilters((prevState) => ({ ...prevState, page, pageSize }));
    },
    pageSizeOptions: [10, 20, 50],
    showSizeChanger: true,
    onShowSizeChange: (current, size) => setUrlFilters((prevState) => ({ ...prevState, page: 1, pageSize: size })),
  };

  /**
   * filtrele kısmında "formu dolduran" input alanında çıkacak isim önerileri, her bir farklı isim
   * dizide sadece bir kere yer alacak şekilde filtreleniyor.
   */

  const handleScroll = useCallback(() => {
    scrollIntoView(document.querySelector('.scroll-row'), {
      align: {
        top: 0,
        left: 0,
      },
    });
  }, []);

  const handleExportTable = async () => {
    setWait(true);
    const fileName = `${committees?.name ?? 'Tüm'} ${subTitle ? '- ' + subTitle : 'Teklifler'}`;
    const url = convertToExportUrl(downloadURL);
    await exportTable(url, fileName);
    setWait(false);
  };

  const hasOffers = offers?.['hydra:member']?.length > 0 || offers?.length > 0;

  // Ödeme raporu uç noktası yalnız versiyon listesinde var; /article_offers/... ile beslenen alt sayfalarda sunulmaz.
  const canExportPaymentReport = isDisposalPaymentFeatureEnabled && (isAdmin || isOrderUnit) && downloadURL.startsWith(`${OFFER_VERSIONS_PATH}?`);

  const handleExportPaymentReport = async () => {
    setWait(true);
    const url = downloadURL.replace(OFFER_VERSIONS_PATH, `${OFFER_VERSIONS_PATH}/export/payment_report`);
    await exportTable(url, formatMessage({ id: 'app.export.paymentReport' }));
    setWait(false);
  };

  const exportMenuItems = [
    { key: 'all', label: <FormattedMessage id="app.export.allArticles" />, onClick: handleExportTable },
    { key: 'paymentReport', label: <FormattedMessage id="app.export.paymentReport" />, onClick: handleExportPaymentReport },
  ];

  useEffect(() => {
    if (shouldScroll) {
      setTimeout(handleScroll, 500);
      dispatch(setShouldScroll(false));
    }
  }, [handleScroll, shouldScroll, dispatch]);

  useEffect(() => {
    dispatch(setTableOrder(order));
  }, [order, dispatch]);

  if (titleLoading) <CircularProgress />;

  return (
    <Row
      align="center"
      justify="center"
    >
      <Title>{`${committees?.name ?? 'Tüm'} ${subTitle ? '- ' + subTitle : 'Teklifler'}`}</Title>

      <Col span={24}>
        <div className="offers-table-columns">
          <div className="gx-d-flex gx-justify-content-between gx-aling-items-center">
            <h1 className="gx-h1-lg">
              <FormattedMessage
                id="article.table.title"
                values={{
                  committee: committees ? `- ${committees?.name}` : '',
                  // bu alanda subtitle dolu ise ve id 1 ve 2 değilse 62. satırda bulunan subtitle lar + Madde Teklifleri yazması sağlandı. Bu sayede Taslaklar - Arap Dili Edebiyatı gibi başlıklar yazılabiliyor.
                  subtitle: subTitle !== '' ? subTitle : !higherCommittee.includes(+stringId) ? 'Madde Teklifleri - Tam Liste' : 'Madde Teklifleri ',
                }}
              />
            </h1>

            {canExportPaymentReport ? (
              <Dropdown
                menu={{ items: exportMenuItems }}
                trigger={['click']}
                disabled={!hasOffers || wait}
              >
                <Button
                  loading={wait}
                  icon={<CloudDownloadOutlined />}
                >
                  <FormattedMessage id="app.export" />
                </Button>
              </Dropdown>
            ) : (
              <Button
                loading={wait}
                disabled={!hasOffers}
                onClick={handleExportTable}
                icon={<CloudDownloadOutlined />}
              >
                <FormattedMessage id="app.export" />
              </Button>
            )}
          </div>
          <Row gutter={6}>
            <Col
              xs={18}
              md={11}
            >
              <Search
                name="offerName"
                onSearch={searchName}
                defaultValue={title}
                placeholder={formatMessage({ id: 'article.table.search' })}
                allowClear
                enterButton
              />
            </Col>

            <Col span={6}>
              <Button
                type="primary"
                onClick={() => setIsModalVisible(true)}
                icon={<FilterFilled />}
                className="gx-mr-0"
              >
                <FormattedMessage id="article.filter" />
              </Button>

              {(urlFilters.filterModalURL || urlFilters.filterStatusURL) && (
                <Button
                  type="link"
                  className=""
                  onClick={clearFilters}
                >
                  Temizle
                </Button>
              )}
            </Col>

            <Modal
              title={<FormattedMessage id="article.filter" />}
              open={isModalVisible}
              onOk={() => setIsModalVisible(false)}
              onCancel={() => setIsModalVisible(false)}
              width={900}
              footer={null}
            >
              <FilterModal
                isLoading={isLoading}
                handleCancel={() => setIsModalVisible(false)}
                tableType={subMenus}
                handleSearchOwner={setSearchOwner}
                setUrlFilters={setUrlFilters}
                filterModals={filterModals}
                ownerOptions={ownerOptions}
              />
            </Modal>

            <div
              style={{
                bottom: offers?.['hydra:member']?.length > 0 || offers?.length > 0 ? -25 : -20,
              }}
              className="gx-position-absolute "
            >
              {urlFilters?.filterTags?.map(
                (item) =>
                  item.isTouched && (
                    <Tag
                      key={item.value}
                      onClick={() => setIsModalVisible(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      {' '}
                      {filterNames[item.value]}{' '}
                    </Tag>
                  )
              )}
            </div>
          </Row>
          <Table
            bordered
            columns={columns}
            pagination={pagination}
            dataSource={offers && (offers['hydra:member'] || offers)}
            rowKey={(record) => (isNotVersionData ? record.id : record.targetOffer?.id)}
            rowClassName={(record) => {
              const recID = isNotVersionData ? record.id : record.targetOffer?.id;
              return recID === scrollID && 'scroll-row';
            }}
            onRow={(record) => ({
              onClick: () =>
                dispatch(
                  setOfferTable({
                    offerID: isNotVersionData ? record.id : record.targetOffer?.id,
                    offerTableURL: pathname,
                    offerTablePage: urlFilters.page,
                    offerTablePageSize: urlFilters.pageSize,
                    filterModalURL: urlFilters.filterModalURL,
                  })
                ),
            })}
            loading={loading || isLoading}
            scroll={{ x: 990, y: height - 400 }}
            sticky={TABLE_STICKY}
          />
        </div>
      </Col>
      <DecisionAskedModal
        data={decisionModal.data}
        open={decisionModal.visible}
        onCancel={() => setDecisionModal({ visible: false, data: null })}
      />
    </Row>
  );
};

export default Articles;
